/**
 * The runs dialog a budget-exhausted / run-failed notification opens.
 *
 * Mounted once in `AppShell` and driven by the `?agentRuns=<agentUserId>`
 * search param, so it opens from any page and the URL is shareable. It
 * deliberately does NOT go through Agent settings: the people who receive
 * these notifications include the agent's accountable person, who may not
 * hold `manage-agents` and so can't open settings, but the runs routes let
 * them read.
 */

import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import { Button } from '@/components/Button'
import { Modal } from '@/components/Modal'

import { AgentRunsPanel } from './AgentRunsPanel'
import { AgentUsageMeter } from './AgentUsageMeter'
import { isAgentUuid, useAgentUsage } from './use-agent-runs'

interface AgentRunsModalProps {
  /** The agent's user id, or null while closed. */
  agentId: string | null
  onClose: () => void
}

export function AgentRunsModal({ agentId: requestedId, onClose }: AgentRunsModalProps) {
  const { t } = useTranslation('agents')
  // A non-UUID param (hand-edited URL) is treated as closed.
  const agentId = isAgentUuid(requestedId) ? requestedId : null
  // The usage payload carries the agent's name, so the title doesn't need the
  // agent record (which requires `manage-agents`). Same query the meter reads.
  const usage = useAgentUsage(agentId)
  const name = usage.data?.agentName
  // Not found / no access: one message, no usage meter, no retry.
  const unavailable = usage.error instanceof ApiError && usage.error.status === 404

  return (
    <Modal open={agentId != null} onClose={onClose} ariaLabel={t('runsModal.ariaLabel')} size="xl">
      <Modal.Header>
        {name ? t('runsModal.titleNamed', { name }) : t('runsModal.title')}
      </Modal.Header>
      <Modal.Body className="max-h-[calc(100dvh-11rem)] overflow-y-auto">
        {agentId != null && unavailable && (
          <p className="text-sm text-fg-muted">{t('runs.unavailable')}</p>
        )}
        {agentId != null && !unavailable && (
          <div className="flex flex-col gap-5">
            <AgentUsageMeter agentId={agentId} />
            <AgentRunsPanel agentId={agentId} onNavigate={onClose} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={onClose}>
          {t('runsModal.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
